import { useState } from "react";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { submitBulkInquiry } from "@/lib/growth.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export function BulkInquiryForm() {
  const send = useServerFn(submitBulkInquiry);
  const [company, setCompany] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [seats, setSeats] = useState("5");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await send({
        data: {
          company: company.trim(),
          contactName: contactName.trim(),
          email: email.trim(),
          seats: Math.max(1, Math.min(100000, Number(seats) || 1)),
          message: message.trim() || null,
        },
      });
      if (!res.ok) {
        toast.error(res.message);
        return;
      }
      setSent(true);
      toast.success("Sent — we'll come back with a quote.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't send that");
    } finally {
      setBusy(false);
    }
  };


  if (sent) {
    return (
      <div className="soft-panel p-7">
        <p className="micro-label">Received</p>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Thanks — we have your details. Expect a quote and an onboarding plan within one business
          day.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="soft-panel space-y-4 p-7">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="bulk-company">Company</Label>
          <Input
            id="bulk-company"
            required
            maxLength={200}
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="bulk-name">Your name</Label>
          <Input
            id="bulk-name"
            required
            maxLength={200}
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="bulk-email">Work email</Label>
          <Input
            id="bulk-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="bulk-seats">Licenses</Label>
          <Input
            id="bulk-seats"
            type="number"
            min={1}
            max={100000}
            required
            value={seats}
            onChange={(e) => setSeats(e.target.value)}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="bulk-message">Anything we should know</Label>
        <Textarea
          id="bulk-message"
          rows={3}
          maxLength={4000}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Teams, rollout timing, invoicing preferences…"
        />
      </div>
      <Button type="submit" disabled={busy} className="w-full sm:w-auto">
        {busy ? "Sending…" : "Request a quote"}
      </Button>
    </form>
  );
}
